import { DOCUMENT, registerLocaleData } from '@angular/common';
import localeEsUS from '@angular/common/locales/es-US';
import {
    APP_INITIALIZER,
    EnvironmentProviders,
    inject,
    LOCALE_ID,
    Provider,
} from '@angular/core';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { TranslocoHttpLoader } from 'app/core/transloco/transloco.http-loader';
import { environment } from 'environments/environment';
import { Settings } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { APP_LANGS, DEFAULT_LANG } from './i18n.types';
import { LanguageService } from './language.service';
import { TranslatedDatepickerIntl } from './translated-datepicker-intl';
import { TranslatedPaginatorIntl } from './translated-paginator-intl';

// en-US ships with Angular; es-US keeps US money/number formatting with Spanish names (D3).
registerLocaleData(localeEsUS, 'es-US');

export const APP_DATE_FORMATS: MatDateFormats = {
    // D15: typed dates keep the US month/day order in both languages
    // (exactly today's en-US 'D' behaviour; es-US 'D' would read d/M/yyyy).
    parse: { dateInput: ['M/d/yyyy', 'M/d/yy'] },
    display: {
        dateInput: 'DDD',
        monthYearLabel: 'LLL yyyy',
        dateA11yLabel: 'DD',
        monthYearA11yLabel: 'LLLL yyyy',
    },
};

/**
 * Everything language-dependent, resolved once per page load (D2).
 * Keep `DateAdapter → LuxonDateAdapter` next to it in `app.config.ts`.
 */
export function provideI18n(): (Provider | EnvironmentProviders)[] {
    return [
        { provide: LOCALE_ID, useFactory: () => inject(LanguageService).locale },
        { provide: MAT_DATE_LOCALE, useFactory: () => inject(LanguageService).locale },
        { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
        { provide: MatPaginatorIntl, useClass: TranslatedPaginatorIntl },
        { provide: MatDatepickerIntl, useClass: TranslatedDatepickerIntl },
        provideTransloco({
            config: {
                availableLangs: [...APP_LANGS],
                defaultLang: DEFAULT_LANG,
                fallbackLang: DEFAULT_LANG,
                missingHandler: {
                    useFallbackTranslation: true,
                    logMissingKey: !environment.production,
                },
                reRenderOnLangChange: false,
                prodMode: environment.production,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            provide: APP_INITIALIZER,
            multi: true,
            useFactory: () => {
                const transloco = inject(TranslocoService);
                // Constructing it strips ?lang= before the router's first navigation.
                const language = inject(LanguageService);
                const document = inject(DOCUMENT);

                return () => {
                    transloco.setActiveLang(language.current);
                    document.documentElement.lang = language.current;
                    Settings.defaultLocale = language.locale;

                    // Spanish is always loaded, so fallback lookups are synchronous (D4):
                    // with `useFallbackTranslation`, loading a non-fallback language fetches
                    // it together with `es` in one forkJoin. Loading `es` separately as well
                    // would request es.json twice.
                    // Transloco completes without a value (or errors) once a file and its
                    // fallback fail, and evicts the failed load, so calling it again refetches.
                    const load = () => firstValueFrom(transloco.load(language.current));

                    // A missing file (network, 5xx, stale deploy) must not abort bootstrap:
                    // retry once, then start anyway rather than showing a blank page.
                    return load()
                        .catch(() => load())
                        .catch((error) =>
                            console.error('[i18n] Translations could not be loaded; starting without them.', error)
                        );
                };
            },
        },
    ];
}
